export function regDateToUi(regDate) {
    const digits = String(regDate == null ? "" : regDate).replace(/\D/g, "");
    if (digits.length < 8) return "";
    const s = digits.slice(0, 8);
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

export function yearMonthToUi(ym) {
    const digits = String(ym == null ? "" : ym).replace(/\D/g, "");
    if (digits.length < 6) return "";
    const s = digits.slice(0, 6);
    return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

export function uiDayToRegDateInt(ui) {
    const n = parseInt(String(ui).replace(/\D/g, ""), 10);
    return Number.isFinite(n) ? n : null;
}

export function uiMonthToYearMonthInt(ui) {
    const n = parseInt(String(ui).replace(/\D/g, ""), 10);
    return Number.isFinite(n) ? n : null;
}

export function uniqueYearMonthsInDayRange(dateStart, dateEnd) {
    const parse = (str) => {
        const p = String(str).split("-").map((x) => parseInt(x, 10));
        if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
        return new Date(p[0], p[1] - 1, p[2]);
    };
    const s = parse(dateStart);
    const e = parse(dateEnd);
    if (!s || !e || s > e) return [];
    const set = new Set();
    let y = s.getFullYear();
    let m = s.getMonth();
    const endY = e.getFullYear();
    const endM = e.getMonth();
    while (y < endY || (y === endY && m <= endM)) {
        set.add(y * 100 + m + 1);
        m += 1;
        if (m > 11) {
            m = 0;
            y += 1;
        }
    }
    return [...set].sort((a, b) => a - b);
}

export function uniqueYearsInMonthRange(monthStart, monthEnd) {
    const y1 = parseInt(String(monthStart).slice(0, 4), 10);
    const y2 = parseInt(String(monthEnd).slice(0, 4), 10);
    if (Number.isNaN(y1) || Number.isNaN(y2)) return [];
    const lo = Math.min(y1, y2);
    const hi = Math.max(y1, y2);
    const years = [];
    for (let y = lo; y <= hi; y += 1) years.push(y);
    return years;
}

export function rowMatchesProductFilter(apiRow, productCode) {
    const rowCode = apiRow.productCode == null ? "" : String(apiRow.productCode).trim();
    const selectedCode = productCode == null ? "" : String(productCode).trim();
    if (rowCode === "") return true;
    if (selectedCode === "") return true;
    return rowCode.toUpperCase() === selectedCode.toUpperCase();
}

function toFiniteNumber(value) {
    if (value == null || value === "") return 0;
    const normalized = typeof value === "string" ? value.replace(/,/g, "").trim() : value;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
}

export function mapDailyApiToUiRow(apiRow, product) {
    const dateUi = regDateToUi(apiRow.regDate);
    const qty = toFiniteNumber(apiRow.quantity);
    const defectQty = apiRow.defectQty == null ? null : toFiniteNumber(apiRow.defectQty);
    const productCode = apiRow.productCode != null ? String(apiRow.productCode).trim() : "";
    return {
        production_id: `d-${apiRow.ptIdx}-${apiRow.regDate}-${apiRow.productCode || ""}`,
        product_id: product.product_id,
        production_date: dateUi,
        production_qty: qty,
        defect_qty: defectQty,
        _productCode: productCode,
        _granularity: "DAY",
        _editing: false,
        _changed: false,
        _isNew: false,
        _origDate: dateUi,
        _origQty: qty,
        _origDefectQty: defectQty,
        _apiPtIdx: apiRow.ptIdx,
        _apiPtAddr: apiRow.ptAddr,
    };
}

export function mapMonthlyApiToUiRow(apiRow, product) {
    const dateUi = yearMonthToUi(apiRow.yearMonth);
    const qty = toFiniteNumber(apiRow.quantity);
    const defectQty = apiRow.defectQty == null ? null : toFiniteNumber(apiRow.defectQty);
    const productCode = apiRow.productCode != null ? String(apiRow.productCode).trim() : "";
    return {
        production_id: `m-${apiRow.ptIdx}-${apiRow.yearMonth}-${apiRow.productCode || ""}`,
        product_id: product.product_id,
        production_date: dateUi,
        production_qty: qty,
        defect_qty: defectQty,
        _productCode: productCode,
        _granularity: "MONTH",
        _editing: false,
        _changed: false,
        _isNew: false,
        _origDate: dateUi,
        _origQty: qty,
        _origDefectQty: defectQty,
        _apiPtIdx: apiRow.ptIdx,
        _apiPtAddr: apiRow.ptAddr,
    };
}

export function buildDailyUpsertRow(row, product) {
    const regDate = uiDayToRegDateInt(row.production_date);
    const quantity = Number(row.production_qty);
    const rowProductCode = row && row._productCode != null ? String(row._productCode).trim() : "";
    const fallbackCode = product && product.product_code != null ? String(product.product_code).trim() : "";
    const productCode = rowProductCode || fallbackCode || null;
    const base = {
        regDate,
        productCode,
        quantity: Number.isFinite(quantity) ? quantity : row.production_qty,
        defectQty: row.defect_qty != null && row.defect_qty !== "" ? Number(row.defect_qty) : null,
    };
    if (product.default_prod_pt_idx != null && product.default_prod_pt_idx !== "") {
        base.ptIdx = product.default_prod_pt_idx;
    } else if (product.pt_addr) {
        base.ptAddr = product.pt_addr;
    }
    return base;
}

export function buildMonthlyUpsertRow(row, product) {
    const yearMonth = uiMonthToYearMonthInt(row.production_date);
    const quantity = Number(row.production_qty);
    const rowProductCode = row && row._productCode != null ? String(row._productCode).trim() : "";
    const fallbackCode = product && product.product_code != null ? String(product.product_code).trim() : "";
    const productCode = rowProductCode || fallbackCode || null;
    const base = {
        yearMonth,
        productCode,
        quantity: Number.isFinite(quantity) ? quantity : row.production_qty,
        defectQty: row.defect_qty != null && row.defect_qty !== "" ? Number(row.defect_qty) : null,
    };
    if (product.default_prod_pt_idx != null && product.default_prod_pt_idx !== "") {
        base.ptIdx = product.default_prod_pt_idx;
    } else if (product.pt_addr) {
        base.ptAddr = product.pt_addr;
    }
    return base;
}
